import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  RowSelectionOptions,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { styled } from '@mui/material';

interface TableProps<TData> extends RowSelectionOptions<TData> {
  columns: ColumnDef<TData, string>[];
  data: TData[];
  onRowClick?: (row: Row<TData>) => void;
  rowSelection?: RowSelectionState;
  getRowId?: (row: TData, index: number) => string;
}

export function Table<TData>({
  columns,
  data,
  onRowClick,
  enableMultiRowSelection,
  rowSelection,
  onRowSelectionChange,
  getRowId = (_, index) => index.toString(),
}: TableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getRowId,
    enableMultiRowSelection,
    state: {
      rowSelection,
    },
    onRowSelectionChange,
  });

  return (
    <TableContainer component={Paper}>
      <MuiTable stickyHeader sx={{ minWidth: 650 }} size='small' aria-label='a dense table'>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <StyledTableRow
              hover
              key={row.id}
              sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              onClick={() => onRowClick?.(row)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </StyledTableRow>
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.grey['50'],
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));
